import {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PostHogProvider } from "posthog-js/react";
import { StyleRoot } from "@ttc3k/trekker";
import { i18nLanguage, i18nNamespace } from "i18n";
import { I18NContextProvider } from "widgets/shared";
import { ApolloProviderPublic } from "shared/apollo";
import { usePostHogWidget } from "widgets/shared/usePostHogWidget";
import { MapRef } from "react-map-gl";
import styles from "styled-system/styles.css?inline";
import mapboxStyles from "mapbox-gl/dist/mapbox-gl.css?inline";
import { TripperWidgetContextProvider } from "./context/TripperWidgetContext";
import { TripperTrackingProvider } from "./context/TripperTrackingContext";
import tripperStyles from "./styles/tripper.css?inline";
import videojsStyles from "video.js/dist/video-js.css?inline";
import { TripperErrorBoundary } from "./TripperErrorBoundary";
import { deriveAccentColors } from "./accentColorHelpers";
import { TripperDebugPanel } from "./components/DebugPanel/TripperDebugPanel";

export type TripperWidgetProvidersProps = PropsWithChildren & {
  widgetId: string;
  accessId: string;
  itineraryId: string;
  language: i18nLanguage;
  fontFamilyOverride?: string;
  primaryColorOverride?: string;
  preview?: boolean;
  atlasMode?: boolean;
  forceDesktop?: boolean;
  verticalStack?: boolean;
  businessTypeFiltersEnabled?: boolean;
  onAtlasModeControllerReady?: (setAtlasMode: (value: boolean) => void) => void;
  onForceDesktopControllerReady?: (
    setForceDesktop: (value: boolean) => void,
  ) => void;
  onVerticalStackControllerReady?: (
    setVerticalStack: (value: boolean) => void,
  ) => void;
  onBusinessTypeFiltersEnabledReady?: (
    setEnabled: (value: boolean) => void,
  ) => void;
  /** Disable Shadow DOM isolation (e.g. when rendering inside an existing React portal/modal). */
  disableShadowDOM?: boolean;
  /** Show diagnostic debug panel for embed troubleshooting. */
  debug?: boolean;
};

export const TripperWidgetProviders = ({
  widgetId,
  accessId,
  itineraryId,
  language,
  fontFamilyOverride,
  primaryColorOverride,
  preview,
  atlasMode: initialAtlasMode = false,
  forceDesktop: initialForceDesktop = false,
  verticalStack: initialVerticalStack = false,
  businessTypeFiltersEnabled: initialBusinessTypeFiltersEnabled = false,
  onAtlasModeControllerReady,
  onForceDesktopControllerReady,
  onVerticalStackControllerReady,
  onBusinessTypeFiltersEnabledReady,
  disableShadowDOM,
  debug,
  children,
}: TripperWidgetProvidersProps) => {
  const { posthog, initPostHog } = usePostHogWidget();
  const mapRef = useRef<MapRef | null>(null);
  const accentDivRef = useRef<HTMLDivElement | null>(null);
  const [atlasMode, setAtlasMode] = useState(initialAtlasMode);
  const [forceDesktop, setForceDesktop] = useState(initialForceDesktop);
  const [verticalStack, setVerticalStack] = useState(initialVerticalStack);
  const [businessTypeFiltersEnabled, setBusinessTypeFiltersEnabled] = useState(
    initialBusinessTypeFiltersEnabled,
  );

  useEffect(() => {
    onAtlasModeControllerReady?.(setAtlasMode);
    return () => {
      onAtlasModeControllerReady?.(() => {});
    };
  }, [onAtlasModeControllerReady]);

  useEffect(() => {
    onForceDesktopControllerReady?.(setForceDesktop);
    return () => {
      onForceDesktopControllerReady?.(() => {});
    };
  }, [onForceDesktopControllerReady]);

  useEffect(() => {
    onVerticalStackControllerReady?.(setVerticalStack);
    return () => {
      onVerticalStackControllerReady?.(() => {});
    };
  }, [onVerticalStackControllerReady]);

  useEffect(() => {
    onBusinessTypeFiltersEnabledReady?.(setBusinessTypeFiltersEnabled);
    return () => {
      onBusinessTypeFiltersEnabledReady?.(() => {});
    };
  }, [onBusinessTypeFiltersEnabledReady]);

  useEffect(() => {
    // Init the page-wide PostHog singleton once. Per-widget identity is NOT
    // registered as global super-properties (a shared singleton would let the
    // last widget overwrite them) — it is attached per-event via
    // TripperTrackingProvider below.
    initPostHog(`widget-tripper-${accessId}-${widgetId}`);
  }, [accessId, initPostHog, widgetId]);

  const accent = useMemo(
    () =>
      primaryColorOverride ? deriveAccentColors(primaryColorOverride) : null,
    [primaryColorOverride],
  );

  const ctxHeaders = useMemo<Record<string, string>>(
    () => ({
      authorization: `Bearer ${widgetId}`,
      metadata: JSON.stringify({
        type: "ORGANIZATION",
        entityId: accessId,
      }),
      "wa-type": "tripperWidget",
      ...(preview ? { "wa-preview": "true" } : {}),
      ...(typeof window !== "undefined"
        ? { "wa-embed-url": window.location.href }
        : {}),
    }),
    [widgetId, accessId, preview],
  );

  return (
    <ApolloProviderPublic ctxHeaders={ctxHeaders}>
      <PostHogProvider client={posthog}>
        <TripperTrackingProvider
          organizationId={accessId}
          widgetId={widgetId}
          itineraryId={itineraryId}
        >
          <StyleRoot
            styles={[styles, mapboxStyles, tripperStyles, videojsStyles]}
            useShadowDOM={!disableShadowDOM}
            fontFamilyOverride={fontFamilyOverride}
          >
            <I18NContextProvider
              defaultNS={[i18nNamespace.Widgets, i18nNamespace.Tripper]}
              i18nWidgetType={"tripper"}
              i18nLang={language}
            >
              <TripperWidgetContextProvider
                mapRef={mapRef}
                accentDivRef={accentDivRef}
                atlasMode={atlasMode}
                forceDesktop={forceDesktop}
                verticalStack={verticalStack}
                businessTypeFiltersEnabled={businessTypeFiltersEnabled}
              >
                <TripperErrorBoundary>
                  {debug && (
                    <TripperDebugPanel
                      widgetId={widgetId}
                      accessId={accessId}
                    />
                  )}
                  <div
                    className={[
                      "tripper-widget-host",
                      preview && "tripper-preview",
                      atlasMode && "tripper-atlas-mode",
                      forceDesktop && "tripper-force-desktop",
                      verticalStack && "tripper-vertical-stack",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    ref={accentDivRef}
                    style={
                      {
                        display: "flex",
                        flexDirection: "column",
                        flex: "1 1 auto",
                        width: "100%",
                        height: "100%",
                        minHeight: 0,
                        overflow: "hidden",
                        "--tripper-primary": primaryColorOverride ?? "#66800b",
                        "--tripper-marker-color":
                          primaryColorOverride ?? "#66800b",
                        "--tripper-route-line-color":
                          primaryColorOverride ?? "#66800b",
                        "--tripper-place-fill-color":
                          primaryColorOverride ?? "#66800b",
                        "--tripper-font-family":
                          fontFamilyOverride ??
                          "var(--wa-fonts-body, system-ui, sans-serif)",
                        ...(accent && {
                          "--tripper-accent-bold": accent.bold,
                          "--tripper-accent-text": accent.text,
                          "--tripper-accent-bg": accent.bg,
                          "--tripper-accent-border": accent.border,
                          // --tripper-bg-secondary intentionally not overridden —
                          // segment and group headers always use the neutral #F7F6ED default.
                        }),
                      } as CSSProperties
                    }
                  >
                    {children}
                  </div>
                </TripperErrorBoundary>
              </TripperWidgetContextProvider>
            </I18NContextProvider>
          </StyleRoot>
        </TripperTrackingProvider>
      </PostHogProvider>
    </ApolloProviderPublic>
  );
};
